import React, {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Icon, Loader, Modal, Tabs, Tooltip } from '@Sage/hoi-poi-ui';
import { COMPANIES, CONTACTS } from 'constants/Entities';
import { EntityListSelectActions } from 'actions';
import Context from 'managers/Context';
import { getLiteral } from 'utils/getLiteral';
import RecipientsAccountsTab from './RecipientsAccountsTab';
import RecipientsContactsTab from './RecipientsContactsTab';

import './styles.scss';

const TAB_ACCOUNTS = 'accounts';
const TAB_CONTACTS = 'contacts';

const SelectRecipientsModal = memo(
    ({
        isOpen,
        campaignId,
        token: initialToken,
        onDone,
        onCancel,
        companiesToken,
        cancelSelection,
        accountsTotal,
        contactsTotal,
        setActiveEntity,
        syncEntityListSelectionToken,
    }) => {
        const [selectionToken, setSelectionToken] = useState(initialToken || null);
        const [selectionReadyForList, setSelectionReadyForList] = useState(false);
        const [nonSubscribedAccountsCount, setNonSubscribedAccountsCount] = useState(0);
        const [nonSubscribedContactsCount, setNonSubscribedContactsCount] = useState(0);
        const [activeTab, setActiveTab] = useState(TAB_ACCOUNTS);

        const isMountedRef = useRef(false);
        const selectionTokenRef = useRef('');
        const prevIsOpenRef = useRef(false);
        const entitySelectionRequestRef = useRef(0);

        useEffect(() => {
            isMountedRef.current = true;
            return () => {
                isMountedRef.current = false;
            };
        }, []);

        useLayoutEffect(() => {
            if (!isOpen) return;
            setActiveTab(TAB_ACCOUNTS);
            setActiveEntity(COMPANIES);
        }, [isOpen, setActiveEntity]);

        useEffect(() => {
            if (!isOpen) {
                prevIsOpenRef.current = false;
                setSelectionReadyForList(false);
                entitySelectionRequestRef.current += 1;
                return;
            }

            const justOpened = !prevIsOpenRef.current;
            prevIsOpenRef.current = true;

            if (justOpened) {
                setNonSubscribedAccountsCount(0);
                setNonSubscribedContactsCount(0);
                selectionTokenRef.current = initialToken || '';
            }

            setSelectionReadyForList(false);

            const entityConfig = activeTab === TAB_CONTACTS ? CONTACTS : COMPANIES;
            const entity = entityConfig.trueName;
            const requestId = ++entitySelectionRequestRef.current;

            Context.domainManager
                .sendEntityListSelection({
                    selectionForId: campaignId ? Number(campaignId) : 0,
                    selectionFor: 'marketingCampaigns',
                    entity,
                    token: selectionTokenRef.current,
                })
                .then((data) => {
                    if (!isMountedRef.current || requestId !== entitySelectionRequestRef.current)
                        return;
                    const token = data?.token || data?.Token || '';
                    selectionTokenRef.current = token;
                    setSelectionToken(token);
                    syncEntityListSelectionToken({ entity: entityConfig, data });
                })
                .catch(() => {
                    // proceed with empty token — user can still make a selection
                })
                .finally(() => {
                    if (!isMountedRef.current || requestId !== entitySelectionRequestRef.current) {
                        return;
                    }
                    setSelectionReadyForList(true);
                });
        }, [isOpen, activeTab, campaignId, initialToken, syncEntityListSelectionToken]);

        const handleTabChange = useCallback(
            (key) => {
                setSelectionReadyForList(false);
                setActiveTab(key);
                const entity = key === TAB_CONTACTS ? CONTACTS : COMPANIES;
                setActiveEntity(entity);
            },
            [setActiveEntity],
        );

        const handleAccountsNonSubscribedChange = useCallback((count) => {
            setNonSubscribedAccountsCount(count);
        }, []);

        const handleContactsNonSubscribedChange = useCallback((count) => {
            setNonSubscribedContactsCount(count);
        }, []);

        const handleDone = useCallback(() => {
            const token = companiesToken || selectionToken || '';
            const count = (Number(accountsTotal) || 0) + (Number(contactsTotal) || 0);
            onDone?.(token, count);
        }, [accountsTotal, companiesToken, contactsTotal, onDone, selectionToken]);

        const handleCancel = useCallback(() => {
            cancelSelection();
            onCancel?.();
        }, [cancelSelection, onCancel]);

        const isDoneDisabled = nonSubscribedAccountsCount > 0 || nonSubscribedContactsCount > 0;

        const tabs = useMemo(() => {
            const tabIconTitle = (icon, label, isActive) => (
                <Tooltip content={label} placement="top">
                    <div
                        className={`fm-select-recipients-modal__tab-title${isActive ? ' fm-select-recipients-modal__tab-title--active' : ''}`}
                        aria-label={label}
                    >
                        <Icon
                            name={icon}
                            size="large"
                            className="fm-select-recipients-modal__tab-icon"
                        />
                    </div>
                </Tooltip>
            );

            const tabLoader = (
                <div className="fm-select-recipients-modal__tab-loader">
                    <Loader />
                </div>
            );

            let accountsTabContent = null;
            if (activeTab === TAB_ACCOUNTS) {
                accountsTabContent = selectionReadyForList ? (
                    <RecipientsAccountsTab
                        selectionToken={selectionToken}
                        onNonSubscribedSelectionChange={handleAccountsNonSubscribedChange}
                    />
                ) : (
                    tabLoader
                );
            }

            let contactsTabContent = null;
            if (activeTab === TAB_CONTACTS) {
                contactsTabContent = selectionReadyForList ? (
                    <RecipientsContactsTab
                        selectionToken={selectionToken}
                        onNonSubscribedSelectionChange={handleContactsNonSubscribedChange}
                    />
                ) : (
                    tabLoader
                );
            }

            return [
                {
                    key: TAB_ACCOUNTS,
                    title: tabIconTitle(
                        'accounts',
                        getLiteral('label_accounts'),
                        activeTab === TAB_ACCOUNTS,
                    ),
                    content: accountsTabContent,
                },
                {
                    key: TAB_CONTACTS,
                    title: tabIconTitle(
                        'contacts',
                        getLiteral('label_contacts'),
                        activeTab === TAB_CONTACTS,
                    ),
                    content: contactsTabContent,
                },
            ];
        }, [
            activeTab,
            selectionToken,
            selectionReadyForList,
            handleAccountsNonSubscribedChange,
            handleContactsNonSubscribedChange,
        ]);

        if (!isOpen) return null;

        return (
            <Modal
                className="fm-select-recipients-modal"
                isOpen={isOpen}
                size="huge"
                useContentStaticHeight
                title={getLiteral('label_select_recipients')}
                onRequestClose={handleCancel}
                useCornerClose={true}
                overrides={{
                    content: {
                        className: 'fm-select-recipients-modal__content',
                    },
                }}
            >
                <Tabs
                    className="fm-select-recipients-modal__tabs"
                    tabs={tabs}
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    editable={false}
                />
                <div className="fm-select-recipients-modal__footer">
                    <Button type="secondary" onClick={handleCancel}>
                        {getLiteral('action_cancel')}
                    </Button>
                    <Tooltip
                        content={
                            isDoneDisabled
                                ? getLiteral(
                                      'label_marketing_campaigns_recipients_unsubscribed_warning',
                                  )
                                : undefined
                        }
                        placement="top"
                    >
                        <div className="fm-select-recipients-modal__done-wrapper">
                            <Button type="primary" isDisabled={isDoneDisabled} onClick={handleDone}>
                                {getLiteral('action_done')}
                            </Button>
                        </div>
                    </Tooltip>
                </div>
            </Modal>
        );
    },
);

SelectRecipientsModal.displayName = 'SelectRecipientsModal';

SelectRecipientsModal.propTypes = {
    isOpen: PropTypes.bool,
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    token: PropTypes.string,
    onDone: PropTypes.func,
    onCancel: PropTypes.func,
};

function mapStateToProps(state) {
    const companies = state.entityListSelect?.[COMPANIES.entity];
    const contacts = state.entityListSelect?.[CONTACTS.entity];
    return {
        companiesToken: companies?.token || '',
        accountsTotal: Number(companies?.total ?? 0) || 0,
        contactsTotal: Number(contacts?.total ?? 0) || 0,
    };
}

const boundEntityListSelect = (dispatch) => bindActionCreators(EntityListSelectActions, dispatch);

const mapDispatchToProps = (dispatch) => {
    const entityListSelect = boundEntityListSelect(dispatch);
    return {
        cancelSelection: entityListSelect.cancelSelection,
        setActiveEntity: entityListSelect.setActive,
        syncEntityListSelectionToken: entityListSelect.syncEntityListSelectionToken,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectRecipientsModal);
